/**
 * Booking Calendar View Component
 * Week and day calendar views for admin booking management
 */

import { useState } from 'react';
import { format, addDays, startOfWeek, isSameDay, isToday, setHours, setMinutes } from 'date-fns';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import StatusBadge from './StatusBadge';

export default function BookingCalendarView({ bookings, onBookingClick, onSlotClick }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('week'); // 'week' | 'day'

  const timeSlots = Array.from({ length: 10 }, (_, i) => i + 9); // 9 AM to 6 PM

  const goToPrevious = () => {
    if (view === 'week') {
      setCurrentDate(addDays(currentDate, -7));
    } else {
      setCurrentDate(addDays(currentDate, -1));
    }
  };

  const goToNext = () => {
    if (view === 'week') {
      setCurrentDate(addDays(currentDate, 7));
    } else {
      setCurrentDate(addDays(currentDate, 1));
    }
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const getBookingsForSlot = (day, hour) => {
    return bookings.filter(booking => {
      const start = new Date(booking.start_time);
      return isSameDay(start, day) && start.getHours() === hour;
    });
  };

  const BookingBlock = ({ booking }) => {
    const staffColor = booking.staff_color || '#3b82f6';
    const startTime = new Date(booking.start_time);

    return (
      <div
        className="text-xs p-2 rounded mb-1 cursor-pointer hover:opacity-80 transition-opacity"
        style={{
          backgroundColor: staffColor + '20',
          borderLeft: `3px solid ${staffColor}`
        }}
        onClick={(e) => {
          e.stopPropagation();
          onBookingClick(booking);
        }}
      >
        <div className="font-semibold truncate">
          {format(startTime, 'h:mm a')}
        </div>
        <div className="truncate">
          {booking.client_first_name} {booking.client_last_name}
        </div>
        <div className="truncate text-gray-600">{booking.service_name}</div>
        <div className="mt-1">
          <StatusBadge status={booking.status} size="sm" />
        </div>
      </div>
    );
  };

  const WeekView = () => {
    const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
    const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

    return (
      <div className="overflow-x-auto">
        <div className="min-w-[900px]">
          <div className="grid grid-cols-8 border-b">
            {/* Time column header */}
            <div className="p-2 text-center text-sm font-medium text-gray-500 border-r">
              Time
            </div>

            {/* Day headers */}
            {weekDays.map(day => (
              <div
                key={day.toISOString()}
                className={`p-2 text-center border-r ${
                  isToday(day) ? 'bg-primary-50' : ''
                }`}
              >
                <div className="text-sm font-semibold text-gray-900">
                  {format(day, 'EEE')}
                </div>
                <div
                  className={`text-lg font-bold ${
                    isToday(day) ? 'text-primary-600' : 'text-gray-700'
                  }`}
                >
                  {format(day, 'd')}
                </div>
              </div>
            ))}
          </div>

          {/* Time slots */}
          {timeSlots.map(hour => (
            <div key={hour} className="grid grid-cols-8 border-b">
              {/* Time label */}
              <div className="p-2 text-sm text-gray-500 border-r bg-gray-50 flex items-start">
                {format(setHours(new Date(), hour), 'h a')}
              </div>

              {/* Day columns */}
              {weekDays.map(day => {
                const slotBookings = getBookingsForSlot(day, hour);
                const slotDateTime = setMinutes(setHours(day, hour), 0);

                return (
                  <div
                    key={`${day.toISOString()}-${hour}`}
                    className={`min-h-[100px] p-1 border-r hover:bg-gray-50 cursor-pointer relative ${
                      isToday(day) ? 'bg-primary-50 bg-opacity-30' : ''
                    }`}
                    onClick={() => onSlotClick && onSlotClick(slotDateTime, hour)}
                  >
                    {slotBookings.map(booking => (
                      <BookingBlock key={booking.id} booking={booking} />
                    ))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const DayView = () => {
    const timeSlots15Min = [];
    for (let hour = 9; hour < 18; hour++) {
      for (let min = 0; min < 60; min += 15) {
        timeSlots15Min.push({ hour, minute: min });
      }
    }

    const getBookingsForTime = (hour, minute) => {
      return bookings.filter(booking => {
        const start = new Date(booking.start_time);
        return (
          isSameDay(start, currentDate) &&
          start.getHours() === hour &&
          start.getMinutes() === minute
        );
      });
    };

    return (
      <div className="overflow-y-auto max-h-[600px]">
        <div className="grid grid-cols-12 border-b bg-primary-50">
          <div className="col-span-2 p-3 text-center border-r">
            <div className="text-sm font-medium text-gray-500">Time</div>
          </div>
          <div className="col-span-10 p-3">
            <div className="text-center">
              <div className="text-sm font-semibold text-gray-900">
                {format(currentDate, 'EEEE')}
              </div>
              <div className="text-xl font-bold text-primary-600">
                {format(currentDate, 'MMMM d, yyyy')}
              </div>
            </div>
          </div>
        </div>

        {timeSlots15Min.map(({ hour, minute }) => {
          const slotBookings = getBookingsForTime(hour, minute);
          const slotDateTime = setMinutes(setHours(currentDate, hour), minute);
          const showHourLabel = minute === 0;

          return (
            <div
              key={`${hour}-${minute}`}
              className={`grid grid-cols-12 ${showHourLabel ? 'border-t border-gray-300' : 'border-t border-gray-100'}`}
            >
              <div className={`col-span-2 p-2 text-sm text-gray-500 border-r ${showHourLabel ? 'bg-gray-50' : ''}`}>
                {showHourLabel && format(setHours(new Date(), hour), 'h:mm a')}
              </div>
              <div
                className="col-span-10 min-h-[50px] p-1 hover:bg-gray-50 cursor-pointer"
                onClick={() => onSlotClick && onSlotClick(slotDateTime, hour)}
              >
                {slotBookings.map(booking => (
                  <BookingBlock key={booking.id} booking={booking} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header with view switcher and navigation */}
      <div className="flex flex-col sm:flex-row items-center justify-between p-4 border-b gap-4">
        <div className="flex gap-2">
          <button
            onClick={() => setView('week')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              view === 'week'
                ? 'bg-primary-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            Week
          </button>
          <button
            onClick={() => setView('day')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              view === 'day'
                ? 'bg-primary-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            Day
          </button>
        </div>

        <div className="flex items-center gap-4">
          <button
            onClick={goToPrevious}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-700"
          >
            <ChevronLeftIcon className="w-5 h-5" />
          </button>

          <div className="text-center min-w-[200px]">
            <div className="text-sm text-gray-500">{format(currentDate, 'MMMM yyyy')}</div>
            {view === 'week' && (
              <div className="text-xs text-gray-400">
                {format(startOfWeek(currentDate, { weekStartsOn: 1 }), 'MMM d')} -{' '}
                {format(addDays(startOfWeek(currentDate, { weekStartsOn: 1 }), 6), 'MMM d')}
              </div>
            )}
            {view === 'day' && (
              <div className="text-xs text-gray-400">
                {format(currentDate, 'EEEE, MMMM d')}
              </div>
            )}
          </div>

          <button
            onClick={goToNext}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-700"
          >
            <ChevronRightIcon className="w-5 h-5" />
          </button>

          <button
            onClick={goToToday}
            className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Today
          </button>
        </div>
      </div>

      {/* Calendar grid */}
      <div className="p-4">
        {view === 'week' ? <WeekView /> : <DayView />}
      </div>

      {/* Legend */}
      <div className="px-4 py-3 border-t bg-gray-50">
        <div className="flex items-center gap-6 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-primary-100 border-l-4 border-primary-500"></div>
            <span>Click booking to edit</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-gray-50 border border-gray-300"></div>
            <span>Click empty slot to create</span>
          </div>
        </div>
      </div>
    </div>
  );
}
